import type { ComponentType } from "react";
import { Link, matchPath, type RouteObject } from "react-router";
import { useT } from "../i18n/I18nProvider";
import { modules as allModules } from "../modules";
import type { ModuleDefinition, NavEntry } from "../modules/types";
import { EmptyState } from "../ui/feedback";
import { useCan, useClub } from "./club";
import { workScreenFor } from "./landing";
import { MENU, SETTINGS_MENU, type MenuGroup } from "./menu";
import { useInstallationStatus } from "./LicenseIndicator";
import { useLicense } from "./license-context";

export interface NavGroup {
  module: ModuleDefinition;
  entries: NavEntry[];
}

function entryVisible(entry: NavEntry, can: (permission: string | undefined) => boolean): boolean {
  if (!can(entry.permission) || (entry.anyPermission && !entry.anyPermission.some(can))) return false;
  return !entry.backOffice || !entry.backOffice.when.some(can) || entry.backOffice.unless.some(can);
}

/** Sidebar content for a user: modules and entries they may see; modules with nothing to show disappear. */
export function visibleNavigation(
  modules: readonly ModuleDefinition[],
  can: (permission: string | undefined) => boolean,
  isBlocked: (module: ModuleDefinition) => boolean = () => false,
): NavGroup[] {
  return modules
    .filter((module) => !isBlocked(module))
    .map((module) => ({ module, entries: module.nav.filter((entry) => entryVisible(entry, can)) }))
    .filter((group) => group.entries.length > 0);
}

/**
 * Whether the license leaves a module out. Learned from `module_not_licensed` errors and, for people who
 * may open the installation status, from the entitlement list; the server stays the judge.
 */
export function useModuleBlocked(): (module: ModuleDefinition) => boolean {
  const { blockedModules } = useLicense();
  const status = useInstallationStatus().data;
  return (module) => {
    const name = module.licenseModule ?? module.id;
    if (blockedModules.includes(name)) return true;
    return Boolean(status?.enforcement.enabled && status.entitlements.installed.some((installed) => installed.name === name && !installed.entitled));
  };
}

/**
 * Modules that share a sidebar heading (the general ledger sits under billing's "Accounting") show as one section: the entries of
 * neighbouring groups with the same heading are joined under the first. Licensing was already applied per module.
 */
export function mergeSections(groups: readonly NavGroup[]): NavGroup[] {
  return groups.reduce<NavGroup[]>((merged, group) => {
    const previous = merged[merged.length - 1];
    if (previous && previous.module.labelKey === group.module.labelKey) {
      return [...merged.slice(0, -1), { module: previous.module, entries: [...previous.entries, ...group.entries] }];
    }
    return [...merged, group];
  }, []);
}

export function useNavigation(): NavGroup[] {
  const can = useCan();
  const blocked = useModuleBlocked();
  return mergeSections(visibleNavigation(allModules, can, blocked));
}

export interface MenuLink {
  entry: NavEntry;
  labelKey: NavEntry["labelKey"];
}

export interface VisibleMenuGroup extends Omit<MenuGroup, "items"> {
  links: MenuLink[];
}

/** The menu definition cut down to what a person sees: links they may open, groups with something in them. */
export function visibleMenu(groups: readonly NavGroup[], menu: readonly MenuGroup[]): VisibleMenuGroup[] {
  const entries = new Map(groups.flatMap(({ module, entries }) => entries.map((entry): [string, NavEntry] => [`${module.id}:${entry.id}`, entry])));
  return menu
    .map(({ items, ...group }) => ({
      ...group,
      links: items.flatMap(([key, labelKey]) => {
        const entry = entries.get(key);
        return entry ? [{ entry, labelKey: labelKey ?? entry.labelKey }] : [];
      }),
    }))
    .filter((group) => group.links.length > 0);
}

export function useMenu() {
  const can = useCan();
  const { club } = useClub();
  const blocked = useModuleBlocked();
  const groups = visibleNavigation(allModules, can, blocked); // by module, unmerged: the menu names entries as module:entry
  const today = workScreenFor(club.role, can);
  // Today already is this person's way to their work screen.
  const menu = visibleMenu(groups, MENU).map((group) => ({ ...group, links: group.links.filter((link) => link.entry.to !== today) })).filter((group) => group.links.length > 0);
  return { today, groups: menu, settings: visibleMenu(groups, SETTINGS_MENU) };
}

/** The most specific menu destination for a path (purchasing invoices over purchasing), and where it sits. */
export function activeLink(pathname: string, groups: readonly VisibleMenuGroup[]): { group: VisibleMenuGroup; link: MenuLink } | undefined {
  return groups
    .flatMap((group) => group.links.map((link) => ({ group, link })))
    .filter(({ link }) => matchPath({ path: link.entry.to, end: link.entry.end ?? false }, pathname))
    .sort((a, b) => b.link.entry.to.length - a.link.entry.to.length)[0];
}

export function NoAccess() {
  const t = useT();
  return (
    <EmptyState icon="shield" title={t("access.deniedTitle")} action={<Link className="cc-button cc-button--primary" to="/">{t("access.goHome")}</Link>}>
      {t("access.deniedBody")}
    </EmptyState>
  );
}

function ModuleNotLicensed({ module }: { module: ModuleDefinition }) {
  const t = useT();
  return (
    <EmptyState icon="lock" title={t("license.moduleBlockedTitle")}>
      {t("license.moduleBlockedBody", { module: t(module.labelKey) })}
    </EmptyState>
  );
}

function Guarded({ module, permission, anyPermission, Screen }: { module: ModuleDefinition; permission: string | undefined; anyPermission?: readonly string[]; Screen: ComponentType }) {
  const can = useCan();
  const blocked = useModuleBlocked();
  if (!can(permission) || (anyPermission && !anyPermission.some(can))) return <NoAccess />;
  return blocked(module) ? <ModuleNotLicensed module={module} /> : <Screen />;
}

/** Route objects for every module. Each screen is code-split and wrapped in a permission check, so typing a URL is not a way around the sidebar. */
export function moduleRoutes(modules: readonly ModuleDefinition[]): RouteObject[] {
  return modules.flatMap((module) =>
    module.routes.map<RouteObject>((route) => ({
      path: route.path,
      lazy: async () => {
        const { default: Screen } = await route.load();
        return { Component: () => <Guarded module={module} permission={route.permission} anyPermission={route.anyPermission} Screen={Screen} /> };
      },
    })),
  );
}
